/**
 * @file PointsLayer.tsx
 * @description
 * Pure presentational layer for rendering congressional district points
 * as SVG `<circle>` elements.
 *
 * This component:
 * - Expects `points` as a valid GeoJSON FeatureCollection of points.
 * - Expects a preconfigured D3 `projection` to convert lat/lng to screen coordinates.
 * - Uses `fillForId` to decide the color for a given district point.
 * - Emits hover events so parent components can drive shared tooltip.
 *
 * IMPORTANT ASSUMPTION:
 * Each point feature's `feature.id` is already in the same ID format used
 * by the bill voting data, e.g. `"118-ca-29"` (congress-state-district format).
 * We are *not* doing any string normalization or reformatting here.
 *
 * Props:
 * - onHover(id, evt): called on mouse enter / move.
 * - onLeave(): called on mouse leave.
 */

import type { FeatureCollection } from 'geojson';
import * as React from 'react';

interface PointsLayerProps {
  points: FeatureCollection | null;
  projection: d3.GeoProjection;
  fillForId: (id: string) => string;
  visible: boolean;
  visibleIds?: string[];
  /** Current zoom scale from the parent map (1 == no zoom). Used to adjust point size. */
  zoomScale?: number;

  onHover?: (id: string, e: React.MouseEvent<SVGCircleElement, MouseEvent>) => void;
  onLeave?: () => void;
}

interface PointFeatureProperties {
  district_id?: string;
  GEOID?: string;
  geoid?: string;
}

export function PointsLayer({
  points,
  projection,
  fillForId,
  visible,
  visibleIds,
  onHover,
  onLeave,
  zoomScale,
}: PointsLayerProps) {
  if (!points || !visible) return null;

  // If visibleIds is provided, filter points to only show those IDs
  const shouldFilter = visibleIds !== undefined && visibleIds.length > 0;
  const visibleIdsSet = shouldFilter ? new Set(visibleIds) : null;

  return (
    <g>
      {points.features.map((f, i) => {
        // We assume `f.id` is already something like "118-ca-29" (congress-state-district format).
        // We include a tiny fallback to properties.* so the UI doesn't explode
        // if one odd feature is missing `id`. We do NOT reshape formats.
        const properties = (f.properties as PointFeatureProperties | null) ?? {};
        const fallbackId =
          properties.district_id ?? properties.GEOID ?? properties.geoid ?? String(i);

        const id = (f.id as string) ?? fallbackId;

        // Skip this point if filtering is enabled and this ID is not in the visible set
        if (shouldFilter && visibleIdsSet && !visibleIdsSet.has(id)) {
          return null;
        }

        // Extract coordinates from point geometry
        const geometry = f.geometry;
        if (geometry.type !== 'Point') return null;

        const [lng, lat] = geometry.coordinates;
        const projected = projection([lng, lat]);

        if (!projected) return null;

        const [x, y] = projected;

        //calculates radius of points as an inverse of zoom
        const baseOnScreen = 3; // pixels at zoom=1, matches original point size
        const k = zoomScale && zoomScale > 0 ? zoomScale : 1;
        const r = Math.max(0.5, baseOnScreen / k);

        return (
          <circle
            key={id}
            cx={x}
            cy={y}
            r={r}
            fill={fillForId(id)}
            stroke="#ffffff"
            strokeWidth={Math.max(0.3, 0.5 / k)}
            onMouseEnter={(e) => onHover?.(id, e)}
            onMouseMove={(e) => onHover?.(id, e)}
            onMouseLeave={onLeave}
          />
        );
      })}
    </g>
  );
}
